import { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { approveFromEmail } from "../../store/slices/emailApprovalslice";
import "./ApprovalPage.css";

const useQuery = () => new URLSearchParams(useLocation().search);

const ApprovalPage = () => {
  const dispatch = useDispatch();
  const { token } = useParams();
  const query = useQuery();

  const statusParam = query.get("status")?.toLowerCase();

  const { approveData, approveError } = useSelector(
    (state) => state.emailApproval
  );


  useEffect(() => {
    if (token && statusParam === "success") {
      dispatch(approveFromEmail({ token }));
    }
  }, [token, statusParam, dispatch]);

  // -----------------------------
  // STATIC STATES
  // -----------------------------
  if (statusParam === "already_used") {
    return renderUI({
      title: "Already Approved",
      description: "This post has already been approved and is scheduled for publishing.",
      icon: "/assets/icons/common/tickgrey.svg",
      status: "success",
    });
  }

  if (statusParam === "expired") {
    return renderUI({
      title: "Link Expired",
      description: (
        <> This approval link is no longer valid.<br /> Please request a new approval link. </>
      ),
      icon: "/assets/icons/common/clock-timeout.svg",
      status: "error",
    });
  }
  if (statusParam === "post_deleted") {
    return renderUI({
      title: "Post no longer available",
      description: "This post has been deleted and can’t be approved anymore.",
      icon: "/assets/icons/common/bin.svg",
      status: "error",
    });
  }

  // -----------------------------
  // API ERROR
  // -----------------------------
  if (approveError) {
    return renderUI({
      title: "Something went wrong",
      description: (
        <> We couldn’t complete your request.<br/> Please try again or open the dashboard.</>
      ),
      icon: "/assets/icons/common/warning.svg",
      status: "error",
    });
  }

  // -----------------------------
  // OPTIMISTIC UI
  // -----------------------------
  if (statusParam === "success" && !approveData) {
    return null;
  }

  // -----------------------------
  // NORMALIZE API RESPONSE
  // -----------------------------
  const response = approveData?.data || {};
  const code = approveData?.code?.toLowerCase();

  const { campaignName, posts, clientName } = response;

  let ui = {
    title: "Something went wrong",
    description: (
        <> We couldn’t complete your request.<br/> Please try again or open the dashboard.</>
    ),
    icon: "/assets/icons/common/warning.svg",
    status: "error",
  };

  // -----------------------------
  // FINAL UI DECISION
  // -----------------------------
  if (code === "approved") {
    if (posts <= 1) {
      ui = {
        title: "Post Approved",
        description: campaignName
          ? `"${campaignName}" has been approved and will be published as scheduled.`
          : "Your post has been approved and will be published as scheduled.",
        icon: "/assets/icons/common/mail-tick.svg",
        status: "success",
      };
    } else if (campaignName && posts > 1) {
      ui = {
        title: "Campaign Approved",
        description: `All ${posts} posts in "${campaignName}" have been approved.`,
        icon: "/assets/icons/common/mail-tick.svg",
        status: "success",
      };
    } else if (!campaignName && posts > 1) {
      ui = {
        title: "All Posts Approved",
        description: `All ${posts} posts across all posts have been approved.`,
        icon: "/assets/icons/common/mail-tick.svg",
        status: "success",
      };
    }
  }

  return renderUI({ ...ui, clientName });
};

// -----------------------------
//  UI COMPONENT
// -----------------------------
const renderUI = ({ title, description, icon, status, clientName }) => (
  <div className="approval-container">
    <div className="approval-logo">
      <img src="/assets/brand/soho_social.svg" alt="logo" />
    </div>

    <div className="approval-card">
      <div className={`approval-icon ${status}`}>
        <img src={icon} alt="status" />
      </div>

      <h2 className="approval-title">{title}</h2>
      <p className="approval-description">{description}</p>

      <div className="approval-divider" />

      {clientName && (
        <p className="approval-client">{clientName}</p>
      )}
    </div>

    <div className="approval-footer">
      <p>Changes will be saved immediately.</p>
      <button onClick={() => window.close()}>
        Close this window
      </button>
    </div>
  </div>
);

export default ApprovalPage;